import React from 'react';
import {ConnectedNovaInvocationControl} from './ConnectedNovaInvocationControl';
import {NovaAgentPickerForTask} from './NovaAgentPickerForTask';
import {hasPermission, hasTaskUpdatePermission, isClientUser} from '../../util/PermissionsUtil';
import {PERMISSION_TYPE} from '../../../../Permissions';

/** What the control needs off a task, on either of the Sprints page's two surfaces. */
export interface SprintNovaControlTask {
	id: string;
	name?: string;
	companyTaskId?: string | number;
	hasNovaConversation?: boolean;
	project: {companyProjectId?: string | number; projectColor?: string};
}

export interface SprintNovaControlProps {
	task: SprintNovaControlTask;
	/**
	 * Which surface this control is mounted on. One task can appear twice on the Sprints page - a
	 * sprint row and the backlog card opposite it - and each needs its own caret id and its own
	 * picker, so the two surfaces must pass different keys.
	 */
	instanceKey: string;
	readOnly?: boolean;
	/** Opens the task. The control has no DOM event to hand over, so neither surface's own click
	 * handler can serve here - each passes what it would do with the task id. */
	onOpenTask: () => void;
}

/**
 * The Nova control as the Sprints page mounts it, on a sprint row or on a backlog card.
 *
 * The two surfaces had this setup written out twice: the same task fields, the same two permission
 * reads, and - the one that bites - the instance key, which has to be the same on the control and
 * on the picker handed to it or the caret opens nothing. Keeping them together here is what stops
 * the pair drifting apart; only the surface's own wrapper and what it does to open a task differ.
 */
export const SprintNovaControl = ({task, instanceKey, readOnly, onOpenTask}: SprintNovaControlProps): JSX.Element => (
	<ConnectedNovaInvocationControl
		taskId={task.id}
		taskName={task.name}
		companyProjectId={task.project.companyProjectId}
		companyTaskId={task.companyTaskId}
		hasConversation={task.hasNovaConversation}
		projectColor={task.project.projectColor}
		instanceKey={instanceKey}
		readOnly={!!readOnly}
		isClientActionsRestricted={isClientUser() && hasPermission(PERMISSION_TYPE.CLIENT_RESTRICTED_TASK_ACTIONS)}
		canEditTask={hasTaskUpdatePermission()}
		onOpenTask={onOpenTask}
		agentPicker={<NovaAgentPickerForTask taskId={task.id} instanceKey={instanceKey} />}
	/>
);
